import React from 'react'
import { ConfigurableProductButtonFrame, InlineConfigurableProductButtonFrame } from './ConfigurableProductView'
import classNames from 'classnames'
import {
  PlaceholderInterface,
  PlaceholderInterfaceError,
  PlaceholderInterfaceHollow,
  PlaceholderInterfaceMech
} from '../data/placeholderImages'
import { IconicColor } from '../types/productTypes'
import FillImageView, { FillImageViewState, StatelessFillImageView } from './FillImageView'
import { getProductImageUrl } from '../products/getProductImageUrl'
import { ButtonImageOverflowScaleMultiplier } from './ProductImageConstants'


export interface ConfigurableProductButtonImagesProps {
  x: number
  y: number
  baseScale: number
  backgroundImage?: string | null
  foregroundImage?: string | null
  onClick?: (e: React.MouseEvent) => void
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void
  selected?: boolean
  tooltip?: string | null
  absoluteSize?: string
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  zIndex?: number
  debugColorCode: IconicColor | null
  shadowScale?: number
  alwaysShowTooltip?: boolean
  setSelected?: (index: number) => void
  selectedButtonIndex?: number
}

const ConfigurableProductButton: React.FC<ConfigurableProductButtonImagesProps> = ({
  x,
  y,
  baseScale,
  onClick,
  onDrop,
  backgroundImage,
  foregroundImage,
  selected,
  tooltip,
  absoluteSize,
  onDragStart,
  onDragOver,
  zIndex,
  debugColorCode,
  shadowScale,
  alwaysShowTooltip
}) => {
  const [draggingOver, setDraggingOver] = React.useState(false)

  const isDropTarget = !!onDrop

  React.useEffect(() => {
    if (!isDropTarget) {
      setDraggingOver(false)
    }
  }, [isDropTarget])

  const onDragOverHandler = React.useMemo(() => {
    if (isDropTarget) {
      return (e: React.DragEvent<HTMLDivElement>) => {
        if (onDragOver) {
          onDragOver(e)
          setDraggingOver(true)
        }
      }
    }

    return undefined
  }, [isDropTarget, onDragOver])

  const onDropHandler = React.useMemo(() => {
    if (onDrop) {
      return (e: React.DragEvent<HTMLDivElement>) => {
        if (onDrop) {
          onDrop(e)
          setDraggingOver(false)
        }
      }
    }

    return undefined
  }, [onDrop])

  const onDragLeave = React.useCallback(() => setDraggingOver(false), [])

  const _backgroundImage = getProductImageUrl(backgroundImage) //!backgroundImage ? PlaceholderInterfaceMech : undefined
  const _foregroundImage = getProductImageUrl(foregroundImage === 'USB' ? PlaceholderInterfaceHollow : foregroundImage || PlaceholderInterface)

  // const [backgroundState, setBackgroundState] = React.useState(FillImageViewState.Loading)
  const [foregroundState, setForegroundState] = React.useState(FillImageViewState.Loading)

  const content = (
    <>
      <div
        className={classNames('ConfigurableProductButton', debugColorCode)}
        style={{
          // borderRadius: '20%',
          overflow: 'hidden',
          boxShadow: `0 ${(shadowScale ?? 1)}px ${shadowScale ?? 1}px 1px #0008`,
        }}
        onClick={onClick}
        onDragOver={onDragOverHandler}
        onDrop={onDropHandler}
        onDragLeave={onDragLeave}
        onDragExit={onDragLeave}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
      >
        {_backgroundImage && (
          <FillImageView
            style={{
              opacity: foregroundState === FillImageViewState.Loading ? 0 : undefined,
              transform: `scale(${ButtonImageOverflowScaleMultiplier})`,
            }}
            // state={backgroundState}
            // setState={setBackgroundState}
            className={classNames('backgroundImage')}
            src={_backgroundImage}
            placeholder={PlaceholderInterfaceMech}
          />
        )}
        {_foregroundImage && (
          <StatelessFillImageView
            state={foregroundState}
            setState={setForegroundState}
            className={classNames('foregroundImage')}
            src={_foregroundImage}
            style={{
              transform: `scale(${ButtonImageOverflowScaleMultiplier})`,
            }}
            placeholder={PlaceholderInterfaceError}
          />
        )}
        <div className="debugColorContainer" />
      </div>
      {tooltip && (
        <div
          key={alwaysShowTooltip ? 'alwaysShowTooltip' : 'tooltip'}
          className={classNames('tooltip', { shown: alwaysShowTooltip, left: x < 40, right: x > 60 })}
        >{tooltip}
        </div>
      )}
    </>
  )

  if (absoluteSize) {
    return (
      <InlineConfigurableProductButtonFrame
        size={absoluteSize}
        highlight={draggingOver || selected}
        clickable={!!onClick}
      >
        {content}
      </InlineConfigurableProductButtonFrame>
    )
  }

  return (
    <ConfigurableProductButtonFrame
      x={x}
      y={y}
      scale={baseScale}
      highlight={draggingOver || selected}
      clickable={!!onClick}
      zIndex={zIndex}
    >
      {content}
    </ConfigurableProductButtonFrame>
  )
}

export default ConfigurableProductButton
